/**
 * Компонент для защиты роутов
 * Автор: Cursor Generator
 * Описание: Проверяет авторизацию и показывает страницу логина при необходимости
 */

import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import Login from '../pages/Login';
import { API_URL } from '../utils/api.js';
import { supabase, authHelpers } from '../lib/supabase.js';

export default function AuthGuard({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [passwordSet, setPasswordSet] = useState(false);
  const [checking, setChecking] = useState(true);
  const location = useLocation();

  useEffect(() => {
    checkAuth();
  }, [location]);

  const checkAuth = async () => {
    try {
      // Сначала проверяем Supabase Auth (приоритет)
      if (supabase) {
        const session = await authHelpers.getSession();
        if (session && session.access_token) {
          setIsAuthenticated(true);
          // Сохраняем информацию о пользователе
          const user = await authHelpers.getUser();
          if (user) {
            localStorage.setItem('user', JSON.stringify({
              id: user.id,
              email: user.email,
              name: user.user_metadata?.name || user.email?.split('@')[0] || 'Пользователь'
            }));
          }
          setChecking(false);
          return;
        }
      }

      // Fallback на старую систему авторизации (для обратной совместимости)
      const sessionToken = localStorage.getItem('session_token') || 
                          document.cookie.split('; ').find(row => row.startsWith('session_token='))?.split('=')[1];

      if (sessionToken) {
        axios.defaults.headers.common['x-session-token'] = sessionToken;
      }

      const sessionResponse = await axios.get(`${API_URL}/user/session`, {
        params: { token: sessionToken },
      });

      if (sessionResponse.data.authenticated) {
        setIsAuthenticated(true);
        // Сохраняем информацию о пользователе
        if (sessionResponse.data.user) {
          localStorage.setItem('user', JSON.stringify(sessionResponse.data.user));
        }
      } else {
        setIsAuthenticated(false);
        // Очищаем недействительный токен
        localStorage.removeItem('session_token');
        localStorage.removeItem('user');
        delete axios.defaults.headers.common['x-session-token'];
        // Перенаправляем на главную страницу
        if (window.location.pathname !== '/' && window.location.pathname !== '/home') {
          window.location.href = '/';
        }
      }
    } catch (error) {
      console.error('Ошибка проверки авторизации:', error);
      setIsAuthenticated(false);
      localStorage.removeItem('session_token');
      localStorage.removeItem('user');
      delete axios.defaults.headers.common['x-session-token'];
      // Перенаправляем на главную страницу
      if (window.location.pathname !== '/' && window.location.pathname !== '/home') {
        window.location.href = '/';
      }
    } finally {
      setChecking(false);
    }
  };

  const handleLogin = (sessionToken) => {
    if (sessionToken) {
      localStorage.setItem('session_token', sessionToken);
      axios.defaults.headers.common['x-session-token'] = sessionToken;
    }
    setIsAuthenticated(true);
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900">
        <div className="text-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            className="text-6xl mb-4"
          >
            ⏳
          </motion.div>
          <p className="text-white text-xl">Загрузка...</p>
        </div>
      </div>
    );
  }

  // Если пользователь не авторизован, перенаправляем на главную
  if (!isAuthenticated) {
    if (window.location.pathname !== '/' && window.location.pathname !== '/home') {
      window.location.href = '/';
      return null;
    }
    return null;
  }

  // Пользователь авторизован, показываем контент
  return children;
}

// Компонент предупреждения о незащищенности
function UnprotectedWarning() {
  const [dismissed, setDismissed] = useState(
    localStorage.getItem('unprotected_warning_dismissed') === 'true'
  );

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('unprotected_warning_dismissed', 'true');
  };

  if (dismissed) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 max-w-md bg-yellow-50 border-2 border-yellow-400 rounded-2xl shadow-lg p-4 z-50">
      <div className="flex items-start">
        <span className="text-2xl mr-3">⚠️</span>
        <div className="flex-1">
          <h4 className="font-bold text-yellow-800 mb-1">Сайт не защищен!</h4>
          <p className="text-sm text-yellow-700 mb-2">
            Ваш сайт доступен без пароля. Установите логин и пароль в настройках для защиты доступа.
          </p>
          <div className="flex gap-2">
            <a
              href="/settings?tab=system"
              className="text-sm bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded-lg font-medium"
            >
              Перейти в настройки
            </a>
            <button
              onClick={handleDismiss}
              className="text-sm text-yellow-700 hover:text-yellow-900"
            >
              Закрыть
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}










